'use client'

import copyToClipboard from 'copy-to-clipboard'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useState } from 'react'

import { Button } from '@/legacy-src/components/ui/button'
import useLinkForTherapist from '@/legacy-src/hooks/useLinkForTherapist'

import { getTranslate } from '../../utilities/i18n'
import Bubble from '../Bubble'
import Icon from '../icons/Icon'
import type { AuthorType } from '../onlineLessons/types'
import { ShareButton } from '../referral/ShareButtons'

type ArticleAuthorProps = {
  author: AuthorType
  isPublic?: boolean
}

const ArticleAuthor: React.FC<ArticleAuthorProps> = ({ author, isPublic = false }) => {
  const linkForTherapist = useLinkForTherapist(author?.therapistId, isPublic)

  return (
    <div className="flex items-center">
      {author?.thumbnail?.data && <Bubble imgUrl={author.thumbnail.data.attributes.url} sizeClass="w-12 h-12" />}

      {(author?.desc || author?.name) && (
        <div className="ml-4 flex flex-col justify-center text-sm">
          <span>{author.desc}</span>
          {linkForTherapist ? (
            <Link className="font-bold text-peach-500 hover:text-peach-500" href={linkForTherapist}>
              <div>{author?.name}</div>
            </Link>
          ) : (
            <strong>{author?.name}</strong>
          )}
        </div>
      )}
    </div>
  )
}
type Props = { isPublic?: boolean; author?: AuthorType; isBusinessPage?: boolean }

const ArticleHeaderInfo: React.FC<Props> = ({ author, isPublic, isBusinessPage }) => {
  const { t } = getTranslate()
  const pathname = usePathname()

  const [isTextCopied, setIsTextCopied] = useState(false)

  const currentUrl = `${process.env.NEXT_PUBLIC_FE_URL}${pathname}`
  const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`

  const copyText = () => {
    copyToClipboard(currentUrl)
    setIsTextCopied(true)
  }

  return (
    <div className="flex-wrap: flex justify-between gap-4 tablet:flex-nowrap">
      {author && <ArticleAuthor author={author} isPublic={isPublic} />}
      <div className="flex items-center gap-4">
        <div className="text-xs font-bold">{t(`component.premiumContent.shareTitle`)}</div>
        <div>
          <div className="flex items-center gap-2">
            <ShareButton
              icon="facebook"
              href={fbShareUrl}
              iconSize="xs"
              newWindow
              variant={isBusinessPage ? 'yellow' : 'default'}
            />
            <Button
              variant={isTextCopied ? 'light' : isBusinessPage ? 'yellow' : 'default'}
              size="icon"
              onClick={copyText}
            >
              <Icon icon="copy" size="xs" />
            </Button>
          </div>
          {isTextCopied && <div className="mt-2 text-right text-xs font-bold">{t(`general.info.copied`)}</div>}
        </div>
      </div>
    </div>
  )
}

export default ArticleHeaderInfo
